import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ICON_TYPES } from '../../../shared/constants';
import { IParentPage } from '../../../types/shared';
import LinkIcon from './shared/LinkIcon';
import { MenuItemButton, MenuItemLink } from './shared/navigation.styles';
import NavigationCancelButton from './shared/NavigationCancelButton';
import { INavigationItem, ISubitem } from './shared/types';

import { ChevronRight } from '../../atoms/icons/ChevronRight';

import { DEQ_TYPES } from '../../../utils/deq';
import { getInternalLink } from '../../../utils/generic';
import { BUTTON_LABELS, languageHelper } from '../../../utils/language';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { H2, H3, IntroText } from '../../../styles/generic';

import ContentWrapper from '../../molecules/content-wrapper/ContentWrapper';

import { useNavigation } from './NavigationContextProvider';

interface Props {
  activeTopItem: INavigationItem;
  onRequestCloseHandler: () => void;
}

const DesktopDropdownMenu = ({
  activeTopItem,
  onRequestCloseHandler,
}: Props) => {
  const [activeSubitem, setActiveSubitem] = useState<ISubitem>();
  useEffect(() => {
    setActiveSubitem(undefined);
  }, [activeTopItem]);
  const { lang } = languageHelper();
  const { positions, setPosition } = useNavigation();

  const updatePosition = (index: number, newPosition: number) => {
    setPosition(index, newPosition);
  };

  const firstPosition = positions[0]?.position;
  const secondPosition = positions[1]?.position;

  return (
    <StyledContentWrapper>
      <NavigationCancelButtonWrapper>
        <NavigationCancelButton
          onClickHandler={() => onRequestCloseHandler()}
        />
      </NavigationCancelButtonWrapper>
      <SidePanel data-testid="desktop-dropdown-side-panel">
        {activeTopItem?.subitems?.map((item, key) => {
          return !item.subitems ? (
            <a
              key={item.displayName}
              href={`/${getInternalLink(item.page)}`}
              onClick={() => {
                updatePosition(1, key);
              }}
              data-element-type={DEQ_TYPES.btnMenu}
              data-component-type={DEQ_TYPES.mainNavigation}
              data-element-label={item.displayName?.toLowerCase() || ''}
              data-element-hierarchy={`${activeTopItem.displayName}|${item.displayName}`.toLowerCase()}
              data-element-target={getInternalLink(item.page)}
              data-element-position={`${firstPosition}|${key}`}
            >
              {item.displayName}
            </a>
          ) : (
            <MenuItemButton
              key={key}
              level={1}
              active={activeSubitem === item}
              onClick={() => {
                updatePosition(1, key);
                setActiveSubitem(item === activeSubitem ? undefined : item);
              }}
              data-testid="side-panel-item"
              data-element-type={DEQ_TYPES.btnMenu}
              data-component-type={DEQ_TYPES.mainNavigation}
              data-element-label={item.displayName?.toLowerCase() || ''}
              data-element-hierarchy={`${activeTopItem.displayName}|${item.displayName}`.toLowerCase()}
              data-element-position={`${firstPosition}|${key}`}
            >
              <span>{item.displayName}</span>
              <ChevronRightWrapper>
                <ChevronRight />
              </ChevronRightWrapper>
            </MenuItemButton>
          );
        })}
      </SidePanel>
      {(activeSubitem as unknown as INavigationItem)?.subitems && (
        <SubItems data-testid="subitems-main-panel">
          <SubItemsTitle>
            <StyledH2 data-testid="active-subitem-header">
              {activeSubitem?.displayName}
            </StyledH2>
            {(activeSubitem?.externalPage || activeSubitem?.page) && (
              <HeaderLink
                href={
                  activeSubitem?.externalPage ||
                  `/${getInternalLink(activeSubitem?.page as IParentPage)}`
                }
                target={activeSubitem?.externalPage ? '_blank' : '_self'}
                rel="noreferrer"
                data-testid="main-subitem"
                data-element-type={DEQ_TYPES.textLink}
                data-element-label={BUTTON_LABELS.viewEverything[
                  lang
                ].toLowerCase()}
                data-element-hierarchy={`${activeTopItem.displayName}|${
                  activeSubitem?.displayName
                    ? `${activeSubitem.displayName}|`
                    : ''
                }${BUTTON_LABELS.viewEverything[lang]}`.toLowerCase()}
                data-component-type={DEQ_TYPES.mainNavigation}
                data-element-target={
                  activeSubitem?.externalPage ||
                  `/${getInternalLink(activeSubitem?.page as IParentPage)}`
                }
              >
                <StyledIntroText>
                  <span>{BUTTON_LABELS.viewEverything[lang]}</span>
                  <LinkIcon
                    iconType={
                      activeSubitem.externalPage
                        ? ICON_TYPES.external
                        : ICON_TYPES.arrowLarge
                    }
                  />
                </StyledIntroText>
              </HeaderLink>
            )}
          </SubItemsTitle>
          <Columns>
            {(activeSubitem as unknown as INavigationItem).subitems.map(
              (item, key: number) => {
                return (item as ISubitem).subitems ? (
                  <Column key={`column-${item.displayName}-${key}`}>
                    <StyledH3
                      data-testid="subitem-display-name"
                      key={`h3-${item.displayName}-${key}`}
                    >
                      {item.displayName}
                    </StyledH3>
                    {(item as ISubitem).subitems.map((subitem, index) => (
                      <StyledTopBarItemLink
                        key={`${subitem.displayName}-${key}`}
                        href={
                          subitem.externalPage ||
                          `/${getInternalLink(subitem?.page as IParentPage)}`
                        }
                        target={subitem.externalPage ? '_blank' : '_self'}
                        data-testid="page-links"
                        data-element-type={DEQ_TYPES.btnMenu}
                        data-component-type={DEQ_TYPES.mainNavigation}
                        data-element-label={
                          subitem.displayName?.toLowerCase() || ''
                        }
                        data-element-hierarchy={`${activeTopItem.displayName}|${
                          activeSubitem?.displayName
                            ? `${activeSubitem?.displayName}|`
                            : ''
                        }${subitem.displayName}`.toLowerCase()}
                        data-element-target={
                          subitem.externalPage ||
                          getInternalLink(subitem?.page as IParentPage)
                        }
                        data-element-position={`${firstPosition}|${secondPosition}|${index}`}
                        isMainPanel
                      >
                        <LinkIcon
                          iconType={
                            subitem.externalPage
                              ? ICON_TYPES.external
                              : ICON_TYPES.arrow
                          }
                        />
                        <LinkText>
                          {subitem.displayName}
                          {subitem.description && (
                            <Description>{subitem.description}</Description>
                          )}
                        </LinkText>
                      </StyledTopBarItemLink>
                    ))}
                  </Column>
                ) : (
                  <a
                    key={key}
                    href={
                      item.externalPage ||
                      `/${getInternalLink(item?.page as IParentPage)}`
                    }
                    data-element-type={DEQ_TYPES.btnMenu}
                    data-component-type={DEQ_TYPES.mainNavigation}
                    data-element-label={item.displayName?.toLowerCase()}
                    data-element-hierarchy={`${activeTopItem.displayName}|${item.displayName}`.toLowerCase()}
                    data-element-target={
                      item.externalPage ||
                      `/${getInternalLink(item?.page as IParentPage)}`
                    }
                    data-element-position={`${firstPosition}|${secondPosition}|${key}`}
                  >
                    <LinkIcon
                      iconType={
                        item.externalPage
                          ? ICON_TYPES.external
                          : ICON_TYPES.arrow
                      }
                    />
                    <LinkText>
                      {item.displayName}
                      {item.description && (
                        <Description>{item.description}</Description>
                      )}
                    </LinkText>
                  </a>
                );
              }
            )}
          </Columns>
        </SubItems>
      )}
    </StyledContentWrapper>
  );
};

export default DesktopDropdownMenu;

const StyledIntroText = styled(IntroText)`
  align-items: center;
  color: inherit;
  display: flex;
  gap: 8px;
`;

const HeaderLink = styled.a`
  text-decoration: none;

  &:hover {
    color: ${Colors.abnGreen65};

    svg {
      transform: translateX(4px);
    }
  }

  &:active {
    color: ${Colors.abnGreen50};
  }
`;

const StyledH2 = styled(H2)`
  margin: 0;
`;

const StyledH3 = styled(H3)`
  margin: 24px 0 8px 0;
`;

const SubItemsTitle = styled.div`
  align-items: center;
  display: flex;
  gap: 32px;
`;

const Columns = styled.div`
  display: flex;
  font-size: 16px;
  gap: 24px;
`;

const Column = styled.div`
  width: 225px;

  ${mq.from.breakpoint.L`
    width: 292px;
  `}
`;

const SidePanel = styled.div`
  border-right: 1px solid ${({ theme }) => theme.colors.abnGreen300};
  display: flex;
  flex-direction: column;
  margin: 64px 0;
  text-align: left;
  width: 222px;
  ${mq.from.breakpoint.L`
    width: 350px;
  `};
`;

const SubItems = styled.div`
  flex-grow: 1;
  height: fit-content;
  overflow-y: auto;
  padding: 64px 0 32px 24px;
`;

const StyledContentWrapper = styled(ContentWrapper)`
  display: flex;
  font-size: 20px;
  max-height: calc(100vh - 112px);
  overflow-y: auto;
  position: relative;
  width: inherit;
  z-index: 4;
`;

const ChevronRightWrapper = styled.div`
  display: inline-flex;
  flex-grow: 1;
  justify-content: end;
  margin-left: 8px;
`;

const Description = styled.span`
  display: none;

  ${mq.from.breakpoint.L`
    color: inherit;
    font-family: ${({ theme }) => theme.fontCondensed};
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    text-align: left;
    display: inline-block;
  `};
`;

const LinkText = styled.span`
  align-content: center;
  display: inline-flex;
  flex-direction: column;
  min-width: 0;
`;

const StyledTopBarItemLink = styled(MenuItemLink)`
  align-items: center;
  flex-direction: row;
  padding-left: 0;

  &:hover {
    color: ${Colors.abnGreen65};

    svg {
      transform: translateX(4px);
    }
  }

  &:active {
    color: ${Colors.abnGreen50};
  }
`;

const NavigationCancelButtonWrapper = styled.div`
  display: block;
  position: absolute;
  right: 24px;
  top: 24px;
`;
